import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { BROWSE_LISTING_KEY, OrderModel, SEARCH_LISTING_KEY } from '@steelbuy/data-access';
import { OrderBuyerModel } from '@steelbuy/ts-shared';
import {
    MarketingBannerContact,
    ButtonCallToAction,
    Icon,
    IconIdentifier,
    ButtonSecondary,
} from '@steelbuy/ui-primitive';
import { createRouteUrl, RoutePath } from '../../router/Routes';
import { FormLayout } from '../../views/layouts/form-layout/FormLayout';
import { scrollMainLayout } from '../app-layout/app-main-layout/AppMainLayout';
import { PageHeader } from '../page-header/PageHeader';

import './CheckoutSuccessDynamicDelivery.scss';

const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);

    // TODO format based on user locale
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Chicago',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });

    return `${formatter.format(date)} CT`;
};

export const CheckoutSuccessDynamicDelivery = ({
    orderModel,
    orderModelCart,
    isCart,
}: {
    orderModel?: Partial<OrderBuyerModel>;
    orderModelCart?: Partial<OrderModel>;
    isCart?: boolean;
}) => {
    const { t } = useTranslation(['translation']);
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const mailAddress = t('constants.emailSupport');
    const purchaseOrderNumber = orderModel?.orderNumber ?? orderModelCart?.purchaseOrderNumber;
    const myOrdersDetailPath = isCart
        ? createRouteUrl(RoutePath.MY_ORDERS_CART, ['cartRefNo', orderModelCart?.steelbuyReferenceNumber as string])
        : createRouteUrl(RoutePath.MY_ORDERS_DETAILS, ['orderId', orderModel?.id as string]);
    const orderDetails = [
        { id: 'orderId', label: t('application.checkoutSuccessDynamicDelivery.orderId'), value: purchaseOrderNumber },
    ];

    if (isCart)
        orderDetails.push({
            id: 'steelbuyReferenceNumber',
            label: t('application.checkoutSuccess.steelbuyReferenceNumber'),
            value: orderModelCart?.steelbuyReferenceNumber,
        });
    else
        orderDetails.push({
            id: 'timestamp',
            label: t('application.checkoutSuccessDynamicDelivery.timestamp'),
            value: formatTimestamp(orderModel?.createdDate as unknown as string),
        });

    const hints = [
        {
            id: 'hint1',
            heading: t('application.checkoutSuccessDynamicDelivery.documentationIssued'),
            helper: t('application.checkoutSuccessDynamicDelivery.documentationIssuedHint'),
        },
        {
            id: 'hint2',
            heading: t('application.checkoutSuccessDynamicDelivery.logisticsUnderway'),
            helper: t('application.checkoutSuccessDynamicDelivery.logisticsUnderwayHint'),
        },
        {
            id: 'hint3',
            heading: t('application.checkoutSuccessDynamicDelivery.realTimeVisibility'),
            helper: t('application.checkoutSuccessDynamicDelivery.realTimeVisibilityHint'),
        },
    ];

    useEffect(() => {
        scrollMainLayout(0, 0);
        queryClient.removeQueries({ queryKey: [BROWSE_LISTING_KEY] });
        queryClient.removeQueries({ queryKey: [SEARCH_LISTING_KEY] });
    }, []);

    return (
        <>
            <PageHeader pageTitle={t('application.checkoutSuccessDynamicDelivery.header.title')} showCartIcon={false} />

            <div className="checkout-success-dynamic-delivery">
                <FormLayout>
                    <div className="checkout-success-dynamic-delivery__wrapper">
                        <section className="checkout-success-dynamic-delivery__success">
                            <img alt="Success" src="/assets/lib-ui-primitive/images/Checkout-SuccessV2.svg" />
                            <div className="checkout-success-dynamic-delivery__success__text">
                                <div>{t('application.checkoutSuccessDynamicDelivery.successText1')}</div>
                                <div>
                                    {orderDetails.map((orderDetail) => (
                                        <div key={orderDetail.id}>
                                            {orderDetail.label}{' '}
                                            <span className="checkout-success-dynamic-delivery__success__text__bold">
                                                {orderDetail.value}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                <div>{t('application.checkoutSuccessDynamicDelivery.successText2')}</div>
                            </div>
                        </section>

                        <section className="checkout-success-dynamic-delivery__hint">
                            <span className="checkout-success-dynamic-delivery__hint__heading">
                                {t('application.checkoutSuccessDynamicDelivery.whatsHappeningNow')}
                            </span>
                            {hints.map((hint) => (
                                <div className="checkout-success-dynamic-delivery__hint__item" key={hint.id}>
                                    <div>
                                        <Icon name={IconIdentifier.OK} />
                                        <div className="checkout-success-dynamic-delivery__hint__item__heading">
                                            {hint.heading}
                                        </div>
                                    </div>
                                    <span className="checkout-success-dynamic-delivery__hint__item__helper">
                                        {hint.helper}
                                    </span>
                                </div>
                            ))}
                        </section>
                    </div>
                    <>
                        <MarketingBannerContact
                            header={t('application.checkoutSuccess.banner.headline')}
                            text={t('application.checkoutSuccess.banner.contactText')}
                        >
                            <div>
                                <span className="marketing-banner-contact__content__body__text">
                                    {t('application.checkoutSuccess.banner.emailLabel')}{' '}
                                </span>
                                <a href={`mailto:${mailAddress}`}>{mailAddress}</a>
                            </div>
                        </MarketingBannerContact>
                        <section className="checkout-success-dynamic-delivery__ctas">
                            <ButtonSecondary
                                label={t('application.checkoutSuccessDynamicDelivery.sourceMaterial')}
                                onClick={() => navigate(RoutePath.DASHBOARD)}
                            />
                            <Link to={myOrdersDetailPath}>
                                <ButtonCallToAction
                                    label={t('application.checkoutSuccessDynamicDelivery.viewOrderDetails')}
                                />
                            </Link>
                        </section>
                    </>
                </FormLayout>
            </div>
        </>
    );
};
